import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ReactSVG } from 'react-svg';
import { CustomButton, Modal, InputBox } from 'ui-controls';
import { PhoneCall } from 'assets';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { AddCompanyMenu } from './AddCompanyMenu';
import { useModalStack } from './ModalStackProvider';
import { useAddPersonMutation } from 'services/rtkQueries/Person/PersonApi';
import { reportException } from 'services/logging';
import { useGetListCompanyQuery } from 'services/rtkQueries';
import Loader from 'components/loader';

const TYPE_OPTIONS = [
  { value: 'Fund Contact', label: 'Fund Contact' },
  { value: 'Investment Team', label: 'Investment Team' },
  { value: 'Consultant', label: 'Consultant' },
  { value: 'Asset Contact', label: 'Asset Contact' },
];

const INITIAL_FORM_VALUES = {
  first_name: '',
  last_name: '',
  company: null,
  person_title: '',
  person_role: '',
  email: '',
  phone_number: '',
  type: null,
};

const AddContactModal = ({ isModalOpen, afterContactAdded = () => {} }) => {
  const [addPerson, { isLoading }] = useAddPersonMutation();
  const { data: companiesList } = useGetListCompanyQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );
  const { pushModal, popModal } = useModalStack();
  const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  // Map companies to options
  const selectCompanyOptions = companiesList?.map((company) => ({
    value: company.company_name,
    label: company.company_name,
    id: company.company_id,
  }));

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  // Handle form submission with double-click prevention
  const handleSubmit = async () => {
    // Prevent double submission
    if (isSubmitting || isSubmittingRef.current) {
      return;
    }

    if (
      !formValues.first_name ||
      !formValues.last_name ||
      !formValues.email ||
      !formValues.type ||
      !formValues.company
    ) {
      toast.error(
        'First Name, Last Name, Email, Type, and Company are required.'
      );
      return;
    }

    setIsSubmitting(true);
    isSubmittingRef.current = true;

    try {
      const payload = { ...formValues };
      const res = await addPerson(payload).unwrap();
      const newContact = res?.data;
      const contactObj = {};
      if (newContact) {
        contactObj.value = newContact.first_name + ' ' + newContact.last_name;
        contactObj.label = newContact.first_name + ' ' + newContact.last_name;
        contactObj.name = newContact.first_name + ' ' + newContact.last_name;
        contactObj.id = newContact.person_id;
        contactObj.email = newContact.email;
      }
      const message = res?.message;
      toast.success(message);
      afterContactAdded(contactObj);
      popModal();
    } catch (error) {
      if (!error?.data?.userMessage) {
        toast.error('An error occurred while adding the contact.');
        reportException(error, 'adding contact');
        return;
      }
      toast.error(error?.data?.userMessage);
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <div className="reference-modal">
      {isSubmitting ? <Loader /> : null}
      <Modal
        isModalOpen={isModalOpen}
        title="Add Contact"
        Icon={<ReactSVG src={PhoneCall} alt="" className="menuIcon" />}
        onClose={popModal}
        style={{
          content: {
            width: '60em',
            height: '55em',
            inset: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: '15px',
            overflow: 'auto',
          },
        }}
      >
        <div className="">
          <div className="form-field">
            <InputBox
              label="First Name:"
              value={formValues.first_name}
              placeholder="Enter First Name"
              onChange={(event) =>
                handleInputChange('first_name', event.target.value)
              }
            />
          </div>
          <div className="form-field">
            <InputBox
              label="Last Name:"
              value={formValues.last_name}
              placeholder="Enter Last Name"
              onChange={(event) =>
                handleInputChange('last_name', event.target.value)
              }
            />
          </div>
          <div className="form-field">
            <div className="label">Company:</div>
            <Select
              value={formValues.company}
              options={selectCompanyOptions}
              isSearchable
              classNamePrefix="react-select-custom"
              className="react-select-container-custom"
              placeholder="Select Company"
              onChange={(option) => handleInputChange('company', option)}
              components={{
                Menu: (props) => AddCompanyMenu(props),
              }}
              styles={{
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
                menu: (base) => ({
                  ...base,
                  marginTop: 0,
                }),
              }}
              menuPlacement="auto"
              menuPosition="absolute"
            />
          </div>
          <div className="form-field">
            <div className="label">Type:</div>
            <Select
              value={formValues.type}
              classNamePrefix="react-select-custom"
              options={TYPE_OPTIONS}
              placeholder="Select Type"
              isSearchable
              onChange={(value) => handleInputChange('type', value)}
            />
          </div>
          <div className="form-field">
            <InputBox
              label="Title:"
              value={formValues.person_title}
              placeholder="Enter Title"
              onChange={(event) =>
                handleInputChange('person_title', event.target.value)
              }
            />
          </div>
          <div className="form-field">
            <InputBox
              label="Role:"
              value={formValues.person_role}
              placeholder="Enter Role"
              onChange={(event) =>
                handleInputChange('person_role', event.target.value)
              }
            />
          </div>
          <div className="form-field">
            <InputBox
              label="Email:"
              value={formValues.email}
              placeholder="Enter Email"
              onChange={(event) =>
                handleInputChange('email', event.target.value)
              }
            />
          </div>
          <div className="emptySpaceVertical"></div>
          <div className="d-flex justify-content-end">
            <CustomButton
              name="Cancel"
              className="editBtn mx-2 review-cancel"
              onClick={popModal}
              disabled={isSubmitting}
            />
            <CustomButton
              name={isSubmitting ? 'Saving...' : 'Save'}
              className="footerPrimaryBtn submit-email mx-2"
              onClick={handleSubmit}
              disabled={isSubmitting}
              isDisable={isSubmitting}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

AddContactModal.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
};

export default AddContactModal;
